<script lang="ts" setup>
import { ref, provide, watch } from 'vue'
import { defineProps } from 'vue'
//echarts
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart, BarChart, GaugeChart } from 'echarts/charts'

import {
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkPointComponent,
} from 'echarts/components'

import VChart, { THEME_KEY } from 'vue-echarts'

const props = defineProps({
  temperature: {
    type: Number,
    required: true,
  },
})

// echarts
use([
  LineChart,
  BarChart,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  DataZoomComponent,
  TransformComponent,
  MarkLineComponent,
  MarkPointComponent,
  GaugeChart,
  CanvasRenderer,
])

provide(THEME_KEY, 'light')

const option = ref({
  series: [
    {
      type: 'gauge',
      center: ['50%', '60%'],
      startAngle: 200,
      endAngle: -20,
      min: 0,
      max: 40,
      splitNumber: 8,
      itemStyle: {
        color: '#FF9800',
      },
      progress: {
        show: true,
        width: 15,
      },
      pointer: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          width: 15,
        },
      },
      axisTick: {
        distance: -25,
        splitNumber: 8,
        lineStyle: {
          width: 1,
          color: '#999',
        },
      },
      splitLine: {
        distance: -30,
        length: 8,
        lineStyle: {
          width: 2,
          color: '#999',
        },
      },
      axisLabel: {
        distance: -15,
        color: '#999',
        fontSize: 10,
      },
      anchor: {
        show: false,
      },
      title: {
        show: false,
      },
      detail: {
        valueAnimation: true,
        width: '50%',
        lineHeight: 30,
        borderRadius: 5,
        offsetCenter: [0, '-20%'],
        fontSize: 18,
        fontWeight: 'bolder',
        formatter: '{value}°C',
        color: 'inherit',
      },
      data: [
        {
          value: props.temperature,
        },
      ],
    },
  ],
})

const chartRef = ref<InstanceType<typeof VChart> | null>(null)

const updateItemStyleGradient = (temp: number) => {
  // Función para determinar el color basado en la temperatura (rango típico del suelo: 15-30°C)
  const getColorForTemperature = (t: number): string => {
    if (t < 10) {
      return '#2196F3' // Azul para muy frío
    } else if (t < 15) {
      return '#4CAF50' // Verde para frío
    } else if (t < 25) {
      return '#FFC107' // Amarillo para óptimo
    } else if (t < 30) {
      return '#FF9800' // Naranja para caliente
    } else {
      return '#F44336' // Rojo para muy caliente
    }
  }

  const newColor = getColorForTemperature(temp)
  option.value.series[0].itemStyle.color = newColor
}

watch(
  () => props.temperature,
  (newTemperature) => {
    option.value.series[0].data[0].value = newTemperature
    updateItemStyleGradient(newTemperature)
  },
  { immediate: true }
)
</script>

<template>
  <v-chart ref="chartRef" class="chart" :option="option" style="height: 200px; width: 100%" />
</template>
